import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Controller,
  FieldError,
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from 'react-hook-form';
import get from 'lodash/get';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';
import { IncomeOptionsDTO, OwnerModelDTO } from '../../api/useIncomeQuery';
import { AssetBucket } from '../components/AssetBucket/AssetBucket';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { getYearAgeRightLabel } from '../../utils/getDominantOwnerForAgeLabel';
import { getRealEstateIncomeOwnerModel } from '../../utils/getRealEstateIncomeOwnerModel';
import { RealEstateFormValues } from './RealEstateForm.meta';

const END_OF_PLAN_END_YEAR = -1;

interface RealEstateFormFieldsProps<TFieldValues extends FieldValues = RealEstateFormValues> {
  formBag: UseFormReturn<TFieldValues>;
  fieldNamePrefix?: string;
  options: IncomeOptionsDTO;
  defaultShowAdvanced?: boolean;
}

export const RealEstateFormFields = <TFieldValues extends FieldValues = RealEstateFormValues>({
  formBag: { control, setValue, watch, formState },
  fieldNamePrefix = '',
  options,
  defaultShowAdvanced = false,
}: RealEstateFormFieldsProps<TFieldValues>) => {
  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  const { RealEstateAssetOptions, OwnerOptions } = options;

  const fieldName = <K extends keyof RealEstateFormValues>(name: K) =>
    `${fieldNamePrefix}${String(name)}` as Path<TFieldValues>;

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const associatedAssetId = watch(fieldName('AssociatedAssetId'));
  const formOwnerModel = watch(`${fieldNamePrefix}OwnerModel` as Path<TFieldValues>) as
    | OwnerModelDTO[]
    | undefined;

  const ownerModel = useMemo(
    () =>
      getRealEstateIncomeOwnerModel({
        associatedAssetId,
        assetOptions: RealEstateAssetOptions,
        formOwnerModel,
      }),
    [associatedAssetId, RealEstateAssetOptions, formOwnerModel],
  );

  const personalInfoOwners = OwnerOptions.People;

  const getRightLabel = useCallback(
    (year: number | null) =>
      year == null
        ? undefined
        : getYearAgeRightLabel(year, ownerModel, personalInfoOwners, primaryYob, spouseYob),
    [ownerModel, personalInfoOwners, primaryYob, spouseYob],
  );

  const managementStyleValue = watch(fieldName('OrionManagementStyleId')) as number | null;
  const endAtEndOfPlan = watch(fieldName('EndAtEndOfPlan'));
  const endAtRetirement = watch(fieldName('EndAtRetirement'));

  const matchesEndYearOption = (
    optionValue: number | null,
    selectedValue: number | null | undefined,
  ) => optionValue === selectedValue || (optionValue == null && selectedValue == null);

  // End of plan incomes are stored with a null EndAge, which would otherwise match "At Retirement".
  const toEndYearOptionValue = (endAge: number | null | undefined) =>
    endAtEndOfPlan || (!endAge && !endAtRetirement && !endAtEndOfPlan)
      ? END_OF_PLAN_END_YEAR
      : endAge;

  const startYearOptions = useMemo(
    () => [
      { value: null, label: 'Start Today' },
      ...options.StartYearOptions.map(year => ({
        value: year,
        label: year,
        rightLabel: getRightLabel(year),
      })),
    ],
    [options.StartYearOptions, getRightLabel],
  );

  // Need Current Year as an option in the End Year picklist for rental income like other income already has present
  const endYearOptions = useMemo(
    () => [
      { value: END_OF_PLAN_END_YEAR, label: 'End of Plan' },
      { value: null, label: 'At Retirement' },
      ...options.OtherIncomeEndYearOptions.map(year => ({
        value: year,
        label: year,
        rightLabel: getRightLabel(year),
      })),
    ],
    [options.OtherIncomeEndYearOptions, getRightLabel],
  );

  return (
    <>
      <Controller
        name={fieldName('AssociatedAssetId')}
        control={control}
        render={({ field: { value, onChange, ...field } }) => (
          <Select
            {...field}
            label="Real Estate Asset"
            value={RealEstateAssetOptions.find(({ value: v }) => v === value)}
            options={RealEstateAssetOptions}
            onChange={option => onChange(option ? option.value : null)}
            formVariant={
              get(formState.errors, fieldName('AssociatedAssetId')) ? 'error' : undefined
            }
            helperText={
              (get(formState.errors, fieldName('AssociatedAssetId')) as FieldError | undefined)
                ?.message
            }
            isPortal
          />
        )}
      />

      <Controller
        control={control}
        name={fieldName('Amount')}
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            {...field}
            label="Pre-Tax Income"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            innerText="/year"
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            formVariant={get(formState.errors, fieldName('Amount')) ? 'error' : undefined}
            helperText={
              (get(formState.errors, fieldName('Amount')) as FieldError | undefined)?.message
            }
            tooltip="This is your pre-tax net income from the property."
          />
        )}
      />

      <ManagementStyle
        control={control}
        options={options}
        fieldName={fieldName('OrionManagementStyleId')}
        onChange={(value: number | null) => {
          setValue(
            fieldName('CustomBucketIndex'),
            value as PathValue<TFieldValues, Path<TFieldValues>>,
          );
        }}
      />

      <AssetBucket
        control={control}
        fieldName={fieldName('CustomBucketIndex')}
        managementStyleValue={managementStyleValue}
        options={options}
      />

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Controller
            control={control}
            name={fieldName('IncomeGrowth')}
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                label="Income Growth"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip='Enter the income growth rate applied to the Pre-Tax Income field. Leave this field blank to use the global "Income Growth Rate" found in Settings.'
              />
            )}
          />

          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            `}
          >
            <Controller
              name={fieldName('StartAge')}
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Select
                  {...field}
                  label="Start Year"
                  value={startYearOptions.find(({ value: v }) => v === value)}
                  options={startYearOptions}
                  onChange={option => onChange(option ? option.value : null)}
                  tooltip="Enter the year when the income will start. To start this income immediately, leave this field blank."
                  isPortal
                />
              )}
            />

            <Controller
              name={fieldName('EndAge')}
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Select
                  {...field}
                  label="End Year"
                  value={endYearOptions.find(({ value: v }) =>
                    matchesEndYearOption(v, toEndYearOptionValue(value)),
                  )}
                  options={endYearOptions}
                  onChange={option => {
                    const selectedValue = option?.value ?? null;
                    onChange(selectedValue);
                    setValue(
                      fieldName('EndAtEndOfPlan'),
                      (selectedValue === END_OF_PLAN_END_YEAR) as PathValue<
                        TFieldValues,
                        Path<TFieldValues>
                      >,
                      { shouldDirty: true },
                    );
                    setValue(
                      fieldName('EndAtRetirement'),
                      (selectedValue === null) as PathValue<TFieldValues, Path<TFieldValues>>,
                      { shouldDirty: true },
                    );
                  }}
                  tooltip="Enter the year when the income will stop. If no year is entered the income will stop at the end of the plan or (if applicable) the sale of the asset."
                  isPortal
                />
              )}
            />
          </Box>

          <Controller
            control={control}
            name={fieldName('YearlyTaxableAmount')}
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                label="Taxable Income"
                decimalScale={2}
                allowNegative={false}
                leftIcon="far fa-dollar-sign"
                innerText="/year"
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip="Enter a taxable amount if the amount to be taxed is different than the pre-tax income."
              />
            )}
          />
        </>
      )}
    </>
  );
};
